/**
 * Event Status Mapping Utility
 * 
 * This file provides a single source of truth for mapping between:
 * - Database values: The actual values stored in the events.status column (constrained by DB check)
 * - UI labels: The user-friendly labels shown in the interface
 * 
 * Database constraint allows: 'draft', 'private', 'public'
 * 
 * Mapping:
 *   DB 'draft'   → UI "Draft"
 *   DB 'public'  → UI "Published"
 *   DB 'private' → UI "Private"
 */

// Valid database values for events.status
export const DB_STATUS = {
  DRAFT: 'draft',
  PUBLIC: 'public',
  PRIVATE: 'private',
};

// UI labels for each database status
export const STATUS_LABELS = {
  [DB_STATUS.DRAFT]: 'Draft',
  [DB_STATUS.PUBLIC]: 'Published',
  [DB_STATUS.PRIVATE]: 'Private',
};

// Reverse mapping: UI label key → DB value
// Used for dropdowns where we need to map friendly names back to DB values
export const LABEL_TO_DB = {
  'Draft': DB_STATUS.DRAFT,
  'Published': DB_STATUS.PUBLIC,
  'Private': DB_STATUS.PRIVATE,
};

// Options for status dropdown in the editor
// Each option has: dbValue (what gets saved) and label (what user sees)
export const STATUS_OPTIONS = [
  { dbValue: DB_STATUS.DRAFT, label: 'Draft' },
  { dbValue: DB_STATUS.PUBLIC, label: 'Published' },
  { dbValue: DB_STATUS.PRIVATE, label: 'Private' },
];

// Filter tabs for the Events list page
// 'all' is a special filter that shows everything
export const STATUS_FILTER_TABS = [
  { key: 'all', label: 'All', dbValue: null },
  { key: 'draft', label: 'Draft', dbValue: DB_STATUS.DRAFT },
  { key: 'published', label: 'Published', dbValue: DB_STATUS.PUBLIC },
  { key: 'private', label: 'Private', dbValue: DB_STATUS.PRIVATE },
];

/**
 * Get the UI label for a database status value
 * @param {string} dbStatus - The status value from the database
 * @returns {string} The user-friendly label
 */
export function getStatusLabel(dbStatus) {
  return STATUS_LABELS[dbStatus] || dbStatus || 'Unknown';
}

/**
 * Get the database value for a UI label
 * @param {string} label - The UI label
 * @returns {string} The database value
 */
export function getDbStatusFromLabel(label) {
  return LABEL_TO_DB[label] || DB_STATUS.DRAFT;
}

/**
 * Check if a status value is valid for the database
 * @param {string} status - The status value to check
 * @returns {boolean} Whether the status is valid
 */
export function isValidDbStatus(status) {
  return Object.values(DB_STATUS).includes(status);
}

/**
 * Normalize a status value to a valid database value
 * Handles legacy values like 'published', 'completed', 'cancelled'
 * @param {string} status - The status value (might be legacy)
 * @returns {string} A valid database status value
 */
export function normalizeToDbStatus(status) {
  // Direct valid values
  if (isValidDbStatus(status)) {
    return status;
  }
  
  // Handle legacy mappings
  const legacyMappings = {
    'published': DB_STATUS.PUBLIC,
    'completed': DB_STATUS.DRAFT,  // Map to draft as fallback (could add separate field later)
    'cancelled': DB_STATUS.DRAFT,  // Map to draft as fallback (could add separate field later)
  };
  
  return legacyMappings[status] || DB_STATUS.DRAFT;
}

/**
 * Get badge styling classes for a status
 * @param {string} dbStatus - The database status value
 * @returns {string} Tailwind CSS classes for the badge
 */
export function getStatusBadgeClasses(dbStatus) {
  const styles = {
    [DB_STATUS.PUBLIC]: 'bg-green-100 text-green-800',
    [DB_STATUS.DRAFT]: 'bg-yellow-100 text-yellow-800',
    [DB_STATUS.PRIVATE]: 'bg-blue-100 text-blue-800',
  };
  
  return styles[dbStatus] || 'bg-gray-100 text-gray-800';
}

/**
 * Filter events by status
 * @param {Array} events - Array of events
 * @param {string} filterKey - The filter key ('all', 'draft', 'published', 'private')
 * @returns {Array} Filtered events
 */
export function filterEventsByStatus(events, filterKey) {
  if (filterKey === 'all') {
    return events;
  }
  
  // Find the tab configuration for this filter
  const tab = STATUS_FILTER_TABS.find(t => t.key === filterKey);
  if (!tab || !tab.dbValue) {
    return events;
  }
  
  return events.filter(event => event.status === tab.dbValue);
}
